import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, FileText, CheckCircle, Loader2, Cpu, Code, X, History } from 'lucide-react';
import CodeAnalysis from './CodeAnalysis.js';
import API from '../../services/api.js';
import ResultsTab from '../../components/ResultsTab.jsx';
import { usePolling } from '../../services/PollingContext.js';

const supportedFormats = [
  '.pt', '.pth', '.bin',
  '.pb',
  '.tflite',
  '.engine', '.plan',
  '.h5', '.keras', '.hdf5',
  '.onnx',
  '.safetensors',
  '.gguf', '.ggml', '.ggmf', '.ggjt', '.ggla', '.ggsa',
  '.msgpack', '.flax', '.orbax', '.jax',
  '.ckpt', '.checkpoint', '.orbax-checkpoint',
  '.pkl', '.pickle', '.dill',
  '.joblib',
  '.npy', '.npz',
  '.pmml',
  '.zip',
  '.manifest',
  '.json'
];

const getFileSignature = (file, path = '') => {
  const normalizedPath = (path || file.webkitRelativePath || file.name || '').toLowerCase();
  return `${normalizedPath}::${file.size}::${file.lastModified}`;
};

const ArchitectureAnalysis = () => {
  const [filesToAnalyze, setFilesToAnalyze] = useState([]);
  const [analysisStatus, setAnalysisStatus] = useState('idle'); // idle, uploading, analyzing, complete, error
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const { startPolling, pollingResult, clearPollingResult, showNotification } = usePolling();
  const showProgressTab = analysisStatus === 'uploading' || analysisStatus === 'analyzing';
  const uploadedSignaturesRef = useRef(new Set());
  const getResultsCacheKey = (assetId) => `model_audit_results_${assetId}`;
  const getSessionRunKey = (assetId) => `model_audit_scan_ran_${assetId}`;


  const handleFileUpload = useCallback(async (file, path = '') => {
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!supportedFormats.includes(fileExtension)) {
      alert(`Unsupported file format. Supported formats: ${supportedFormats.join(', ')}`);
      return;
    }

    const signature = getFileSignature(file, path);
    if (uploadedSignaturesRef.current.has(signature)) {
      return;
    }
    uploadedSignaturesRef.current.add(signature);

    setFilesToAnalyze(prevFiles => [...prevFiles, { file, path }]);
    setAnalysisStatus('idle');
    setActiveTab('upload');
  }, []);

  const handleRemoveFile = (index) => {
    setFilesToAnalyze((prevFiles) => {
      const fileToRemove = prevFiles[index];
      if (fileToRemove) {
        uploadedSignaturesRef.current.delete(getFileSignature(fileToRemove.file, fileToRemove.path));
      }
      return prevFiles.filter((_, i) => i !== index);
    });
  };

  const startAnalysis = async () => {
    showNotification("Model File Scan Started", "info", "Architecture Analysis");
    setAnalysisStatus('uploading');
    setActiveTab('progress');
    setProgress(0);

    try {
      const formData = new FormData();
      filesToAnalyze.forEach(({ file, path }, i) => {
        formData.append('file' + i, file);
        formData.append('path' + i, path);
      });
      const assetId = sessionStorage.getItem('asset_id');
      formData.append('asset_id', assetId);

      const response = await API.post('/upload-model/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Reserve the first 40% for upload, remaining for backend analysis.
          const mappedUploadProgress = Math.min(40, Math.round(percentCompleted * 0.4));
          setProgress(mappedUploadProgress);
        },
      });

      if (response.status === 202) {
        setAnalysisStatus('analyzing');
        setProgress((prev) => Math.max(prev, 45));
        startPolling(response.data.task_id, 'model_audit', process.env.REACT_APP_DJANGO_API_URL);
      } else {
        setAnalysisStatus('error');
      }
    } catch (error) {
      if (error?.response?.status === 409 && error?.response?.data?.duplicate) {
        const duplicateScanId = error.response.data.scan_id;
        alert(`This file has already been scanned in this scan ${duplicateScanId}`);
        const existingResults = await fetchResults({ suppressError: true, scanId: duplicateScanId });
        if (existingResults) {
          const assetId = sessionStorage.getItem('asset_id');
          if (assetId) {
            sessionStorage.setItem(getSessionRunKey(assetId), 'true');
          }
          setAnalysisStatus('complete');
          setActiveTab('results');
        } else {
          setAnalysisStatus('idle');
          setActiveTab('upload');
          setProgress(0);
        }
        fetchHistory();
        return;
      }
      console.error('Upload failed:', error);
      setAnalysisStatus('error');
    }
  };

  const fetchResults = useCallback(async ({ suppressError = false, scanId = null } = {}) => {
    try {
      const assetId = sessionStorage.getItem('asset_id');
      const query = scanId ? `scan_id=${scanId}&asset_id=${assetId}` : `asset_id=${assetId}`;
      const response = await API.get(`/get-model-audit-results/?${query}`);
      setResults(response.data);
      if (assetId) {
        sessionStorage.setItem(getResultsCacheKey(assetId), JSON.stringify(response.data));
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch results:', error);
      if (!suppressError) {
        setAnalysisStatus('error');
      }
      return null;
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    const assetId = sessionStorage.getItem('asset_id');
    if (!assetId) {
      setScanHistory([]);
      return;
    }

    setHistoryLoading(true);
    try {
      const response = await API.get(`/get-model-audit-history/?asset_id=${assetId}`);
      setScanHistory(response.data?.history || []);
    } catch (error) {
      console.error('Failed to fetch model audit history:', error);
      setScanHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pollingResult && pollingResult.taskType === 'model_audit') {
      if (pollingResult.status === 'SUCCESS') {
        setProgress(100);
        setAnalysisStatus('complete');
        setActiveTab('results');
        const assetId = sessionStorage.getItem('asset_id');
        if (assetId) {
          sessionStorage.setItem(getSessionRunKey(assetId), 'true');
        }
        fetchResults();
        fetchHistory();
        clearPollingResult();
      } else if (pollingResult.status === 'FAILURE') {
        setAnalysisStatus('error');
        clearPollingResult();
      }
    }
  }, [pollingResult, clearPollingResult, fetchResults, fetchHistory]);

  useEffect(() => {
    if (analysisStatus !== 'analyzing') {
      return;
    }

    const intervalId = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          return prev;
        }
        const step = Math.max(1, Math.round((95 - prev) / 12));
        return Math.min(95, prev + step);
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [analysisStatus]);

  useEffect(() => {
    const assetId = sessionStorage.getItem('asset_id');
    if (!assetId) {
      return;
    }

    const cachedResults = sessionStorage.getItem(getResultsCacheKey(assetId));
    const hasSessionRun = sessionStorage.getItem(getSessionRunKey(assetId)) === 'true';
    if (cachedResults && hasSessionRun) {
      try {
        const parsed = JSON.parse(cachedResults);
        setResults(parsed);
        setAnalysisStatus('complete');
        setActiveTab('results');
      } catch (error) {
        console.error('Failed to parse cached model audit results:', error);
      }
    }

    fetchHistory();
  }, [fetchResults, fetchHistory]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const items = e.dataTransfer.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i].webkitGetAsEntry();
        if (item) {
          scanFiles(item);
        }
      }
    }
  };

  const scanFiles = (item, path = '') => {
    if (item.isFile) {
      item.file((file) => {
        handleFileUpload(file, path + file.name);
      });
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      dirReader.readEntries((entries) => {
        entries.forEach((entry) => {
          scanFiles(entry, path + item.name + '/');
        });
      });
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      if (e.target.webkitdirectory) {
        // Folder upload
        for (let i = 0; i < files.length; i++) {
          handleFileUpload(files[i], files[i].webkitRelativePath);
        }
      } else {
        // File upload
        for (let i = 0; i < files.length; i++) {
          handleFileUpload(files[i]);
        }
      }
    }
  };

  const downloadSBOM = () => {
    const sbomData = {
      bomFormat: 'CycloneDX',
      specVersion: '1.5',
      serialNumber: 'urn:uuid:' + crypto.randomUUID(),
      version: 1,
      metadata: {
        timestamp: new Date().toISOString(),
        tools: [{ name: 'Architecture Analysis Tool', version: '1.0.0' }],
        component: {
          type: 'machine-learning-model',
          name: filesToAnalyze.map(f => f.file.name).join(', ') || 'model',
          version: '1.0.0'
        }
      },
      components: results?.issues.map(issue => ({
        type: 'library',
        name: issue.location,
        version: issue.details.ml_context?.framework,
        licenses: []
      })) || []
    };

    const blob = new Blob([JSON.stringify(sbomData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filesToAnalyze.map(f => f.file.name).join('_') || 'model'}_sbom.json`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const resetAnalysis = () => {
    const assetId = sessionStorage.getItem('asset_id');
    if (assetId) {
      sessionStorage.removeItem(getResultsCacheKey(assetId));
      sessionStorage.removeItem(getSessionRunKey(assetId));
    }
    setFilesToAnalyze([]);
    uploadedSignaturesRef.current.clear();
    setAnalysisStatus('idle');
    setProgress(0);
    setResults(null);
    setScanHistory([]);
    setActiveTab('upload');
  };

  return (
    <div className="min-h-screen bg-[#0D0D0E] p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
            <Cpu className="w-8 h-8" />
            AI BOM Analysis
          </h1>
          <p className="text-gray-400">AI/ML Model Analysis with SBOM Generation</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex mb-8 border-b border-gray-700">
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-6 py-3 -mb-px text-lg font-semibold transition-all duration-300 border-b-2 focus:outline-none ${
              activeTab === 'upload' 
                ? 'border-blue-400 text-blue-400' 
                : 'border-transparent text-gray-400 hover:text-blue-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Model Analysis
            </div>
          </button>
          {showProgressTab && (
            <button
              onClick={() => setActiveTab('progress')}
              className={`ml-4 px-6 py-3 -mb-px text-lg font-semibold transition-all duration-300 border-b-2 focus:outline-none ${
                activeTab === 'progress' 
                  ? 'border-blue-400 text-blue-400' 
                  : 'border-transparent text-gray-400 hover:text-blue-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5" />
                Progress
              </div>
            </button>
          )}
          {analysisStatus === 'complete' && (
            <button
              onClick={() => setActiveTab('results')}
              className={`ml-4 px-6 py-3 -mb-px text-lg font-semibold transition-all duration-300 border-b-2 focus:outline-none ${
                activeTab === 'results' 
                  ? 'border-blue-400 text-blue-400' 
                  : 'border-transparent text-gray-400 hover:text-blue-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Results
              </div>
            </button>
          )}
          <button
            onClick={() => setActiveTab('code')}
            className={`ml-4 px-6 py-3 -mb-px text-lg font-semibold transition-all duration-300 border-b-2 focus:outline-none ${
              activeTab === 'code'
                ? 'border-blue-400 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-blue-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Code Analysis
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab('history');
              fetchHistory();
            }}
            className={`ml-4 px-6 py-3 -mb-px text-lg font-semibold transition-all duration-300 border-b-2 focus:outline-none ${
              activeTab === 'history'
                ? 'border-blue-400 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-blue-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Scan History
            </div>
          </button>
        </div>

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                dragOver 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="w-16 h-16 mx-auto mb-6 text-gray-400" />
              <h3 className="text-2xl font-semibold mb-2 text-white">Upload Model File or Folder</h3>
              <p className="text-gray-400 mb-6">
                Drag and drop your model file or folder here, or click to browse
              </p>
            <input
              type="file"
              accept={supportedFormats.join(',')}
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload-files"
              multiple
            />

              <label
                htmlFor="file-upload-files"
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg cursor-pointer transition-colors font-semibold"
              >
                <Upload className="w-5 h-5 mr-2" />
                Choose File or Folder
              </label>
            </div>

            <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-4 text-white">Supported Formats:</h4>
              <div className="flex flex-wrap gap-3">
                {supportedFormats.map((format) => (
                  <span
                    key={format}
                    className="px-3 py-1 bg-gray-700/50 border border-gray-600 rounded-full text-sm text-gray-300"
                  >
                    {format}
                  </span>
                ))}
              </div>
            </div>

            {filesToAnalyze.length > 0 && (
              <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
                <h4 className="text-lg font-semibold mb-4 text-white">Selected Files:</h4>
                <div className="space-y-2">
                  {filesToAnalyze.map((f, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 bg-black/30 p-4 rounded-lg group">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-blue-400" />
                        <span className="font-medium text-white">{f.file.name}</span>
                        <span className="text-gray-400">({(f.file.size / 1024 / 1024).toFixed(2)} MB)</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 hover:border-red-500/50 transition-all duration-200 opacity-0 group-hover:opacity-100"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={startAnalysis}
                  disabled={analysisStatus === 'uploading' || analysisStatus === 'analyzing'}
                  className={`mt-2 px-6 py-3 rounded-xl border font-medium min-w-[120px] flex items-center justify-center transition-all duration-300 border-blue-500/70 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/25 hover:scale-[1.02]'`}
                >
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Analyze
                </>                
              </button>
              </div>
            )}
          </div>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div className="space-y-6">
            <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-semibold text-white">Analysis Progress</h3>
                <div className="flex items-center gap-3">
                  {analysisStatus === 'analyzing' && <Loader2 className="w-6 h-6 animate-spin text-blue-400" />}
                  {analysisStatus === 'complete' && <CheckCircle className="w-6 h-6 text-green-400" />}
                  <span className="text-lg text-gray-300">
                    {analysisStatus === 'uploading' && 'Uploading...'}
                    {analysisStatus === 'analyzing' && 'Analyzing...'}
                    {analysisStatus === 'complete' && 'Complete'}
                  </span>
                </div>
              </div>

              <div className="w-full bg-gray-700/50 rounded-full h-3 mb-4">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="text-center text-md text-gray-400">
                {Math.round(progress)}% Complete
              </div>
            </div>

            {filesToAnalyze.length > 0 && (
              <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
                <h4 className="text-lg font-semibold mb-4 text-white">Processing:</h4>
                <div className="space-y-2">
                  {filesToAnalyze.map((f, index) => (
                    <div key={index} className="flex items-center gap-3 bg-black/30 p-4 rounded-lg">
                      <FileText className="w-6 h-6 text-blue-400" />
                      <span className="font-medium text-white">{f.file.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-4 text-white">Analysis Steps:</h4>
              <div className="space-y-3 text-md">
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-2.5 h-2.5 bg-gray-500 rounded-full" />
                  <span>Model Structure Analysis</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-2.5 h-2.5 bg-gray-500 rounded-full" />
                  <span>Dependency Extraction</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-2.5 h-2.5 bg-gray-500 rounded-full" />
                  <span>SBOM Generation (CycloneDX)</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-2.5 h-2.5 bg-gray-500 rounded-full" />
                  <span>Security Assessment</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <div className="w-2.5 h-2.5 bg-gray-500 rounded-full" />
                  <span>Vulnerability Scanning</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && results && (
          <ResultsTab 
            results={results} 
            downloadSBOM={downloadSBOM} 
            resetAnalysis={resetAnalysis} 
          />
        )}
        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-[#1a1a1c] border border-[#232325] rounded-xl p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Model Scan History</h3>
            {historyLoading ? (
              <p className="text-gray-400">Loading history...</p>
            ) : scanHistory.length === 0 ? (
              <p className="text-gray-400">No model scan history available.</p>
            ) : (
              <div className="space-y-3">
                {scanHistory.map((scan) => (
                  <div key={scan.scan_id} className="bg-black/30 border border-[#232325] rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-white font-medium">Scan ID: {scan.scan_id}</p>
                      <p className="text-gray-400 text-sm">Created: {new Date(scan.created_at).toLocaleString()}</p>
                      <p className="text-gray-400 text-sm">Status: {scan.status}</p>
                      <p className="text-gray-400 text-sm">Files Scanned: {scan.files_scanned ?? (Array.isArray(scan.files) ? scan.files.length : 0)}</p>
                      {Array.isArray(scan.files) && scan.files.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm text-blue-300 hover:text-blue-200">
                            View File Details
                          </summary>
                          <div className="mt-2 space-y-2">
                            {scan.files.map((fileEntry, idx) => (
                              <div key={`${scan.scan_id}-file-${idx}`} className="bg-black/40 border border-gray-800 rounded p-2">
                                <p className="text-sm text-gray-200">Name: {fileEntry.file_name || 'N/A'}</p>
                                <p className="text-xs text-gray-400 break-all">Hash: {fileEntry.file_hash || 'N/A'}</p>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        const data = await fetchResults({ scanId: scan.scan_id });
                        if (data) {
                          setAnalysisStatus('complete');
                          setActiveTab('results');
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium"
                    >
                      View Results
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Code Analysis Tab */}
        {activeTab === 'code' && (
          <div className="space-y-6">
            <CodeAnalysis/>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchitectureAnalysis;
